/**
 * The booking flow's URL state — ONE schema for every room-shaped flow.
 *
 * Replaces the two copies under `roomRental/common/url/` and
 * `venueReservation/common/url/`, which held the same booking state under
 * different key names for no behavioural reason (ADR-0026, phase 1 slice B).
 *
 * ── What is unified, and what that cost ───────────────────────────────────
 *
 * The venue schema was a strict SUPERSET of the room one — same structure plus
 * start/end time and event type — so this is the venue schema with canonical
 * names, and the room flow loses nothing.
 *
 * | was (room)     | was (venue) | now            |
 * |----------------|-------------|----------------|
 * | `checkInDate`  | `date`      | `checkInDate`  |
 * | `checkOutDate` | `endDate`   | `checkOutDate` |
 * | `apartmentId`  | `venueId`   | `objectId`     |
 *
 * The date keys keep the room spelling because Redux already calls them that
 * for BOTH flows (`bookingCreation.orderData.checkInDate`), so this removes a
 * translation rather than adding one.
 *
 * `objectId` is a deliberate third name. `apartmentId` would have been the
 * cheaper choice — it is what Redux (`selectedApartment`) and the API
 * (`/apartments/...`) already say — but this shell is meant to carry meeting
 * rooms, halls and desks (ADR-0026 §1), and `apartmentId` is wrong for all
 * three. `venueId` is wrong for a guest apartment. A neutral name is the only
 * one that does not lie about something.
 *
 * ── Old links ─────────────────────────────────────────────────────────────
 *
 * Renamed outright, with NO dual-read shim (PO, 2026-09-22 — plan §5.2). Only a
 * URL captured mid-flow carries these keys: neither the home screen nor either
 * flow's entry path has any query parameters at all. `parseBookingQuery` drops
 * what it does not recognise, so a stale link degrades to the keys that still
 * match rather than failing — and `clampStep` below is what keeps that from
 * landing on an empty screen.
 */

import { isRealCalendarDate } from "../../../../util/date";

/** Query parameters persisted in the URL across a room-shaped booking flow.
 *  All optional: the flow fills them in progressively. */
export interface BookingQuery {
  /**
   * The product category this flow was entered through (ADR-0026 §A/§B).
   *
   * Carried for the whole flow, not just the search, because the category is
   * the CONFIGURATION key: `directBooking`, `eventTypes` and the booking
   * window are resolved category-first (`util/categoryConfig.ts`), and those
   * are read on the later steps. Dropped at step 1 — which is what happened
   * before this key existed — every one of them would silently fall back to
   * the use case, so a tenant's per-category setting would appear to be
   * ignored on exactly the screens it is meant to govern.
   *
   * Same validated shape as at the entry point (`categoryFromSearch`): the
   * value is attacker-controllable and reaches a SQL parameter, so anything
   * malformed is treated as absent rather than passed on.
   */
  category?: string;

  /** Selected city name */
  city?: string;

  /** Selected district name, or `DISTRICT_ALL` for any district in the city */
  district?: string;

  /** Start date, ISO `YYYY-MM-DD`. Redux: `orderData.checkInDate`. */
  checkInDate?: string;

  /** End date, ISO `YYYY-MM-DD`. Omit for a single-day booking. */
  checkOutDate?: string;

  /** Start time, 24-hour `HH:mm`. Time-of-day flows only. */
  startTime?: string;

  /**
   * End time, 24-hour `HH:mm`. On a same-day booking (no `checkOutDate`, or
   * equal to `checkInDate`) it must be later than `startTime`; across days any
   * time of day is valid.
   */
  endTime?: string;

  /** Event type LABEL as displayed (e.g. "Bürgerversammlung") — the standard
   *  localized vocabulary or a tenant-configured free-text entry. */
  eventType?: string;

  /** Number of guests — a positive integer, see `MAX_GUESTS`. */
  numberOfGuests?: number;

  /** The selected bookable object, resolved against the fetched list after
   *  step 1. */
  objectId?: string;

  /** Current step, `0..MAX_STEP`. */
  step?: number;
}

/**
 * Canonical key order, so stringification is deterministic. Follows the flow:
 * location → dates → times → guests → object → step.
 */
export const BOOKING_QUERY_KEYS: readonly (keyof BookingQuery)[] = [
  "category",
  "city",
  "district",
  "checkInDate",
  "checkOutDate",
  "startTime",
  "endTime",
  "eventType",
  "numberOfGuests",
  "objectId",
  "step",
] as const;

/**
 * A sanity ceiling on a URL parameter, NOT the business rule.
 *
 * The two copies disagreed — 50 for rooms, 1000 for venues — and the room
 * constant is how "max. 50 Teilnehmer" reached a customer whose room holds
 * more (innodom, 2026-09-22). The real cap is the object's configured
 * capacity, which the screen enforces; this only stops a nonsense URL from
 * hydrating. Taking the higher of the two is therefore deliberate: a lower
 * ceiling here cannot express a real tenant's capacity, while a higher one
 * cannot let a booking exceed it either.
 *
 * The existing suites assert against this CONSTANT rather than the literal, so
 * widening it does not weaken them.
 */
export const MAX_GUESTS = 1000;

export const MAX_STEP = 5;

/** District sentinel: "any district within the city". */
export const DISTRICT_ALL = "all" as const;

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 24-hour time, optional leading zero on the hour: "9:00" and "09:00" both
 * match. The codebase's own slot generator (`generateHourSlots`) emits the
 * unpadded form, so a stricter pattern would make a URL → Redux round-trip
 * silently revert the customer's selection to the "0" sentinel.
 */
export const TIME_PATTERN = /^(\d|[01]\d|2[0-3]):[0-5]\d$/;

export const ID_PATTERN = /^[a-zA-Z0-9_-]{6,64}$/;
export const NAME_PATTERN = /^[\p{L}\p{M}\d\s\-.,'()]{1,64}$/u;
/**
 * Event types are display LABELS, not slugs: the standard vocabulary is
 * localized German/English ("Bürgerversammlung") and tenants configure their
 * own free-text labels (spaces, umlauts, punctuation — anything printable).
 * The old ASCII-only pattern silently dropped "Bürgerversammlung" on the
 * URL round-trip, so the selection reverted and "Weiter" never enabled
 * (demo2, 2026-09-23). Only control characters are rejected; the length cap
 * mirrors the write-side rule (EVENT_TYPE_MAX_LENGTH = 40 in
 * `new-backend/admin/src/lib/serviceConfiguration.ts`).
 */
/** The longest event label the URL carries — also the free-text name's cap. */
export const EVENT_TYPE_MAX_LENGTH = 40;
export const EVENT_TYPE_PATTERN = new RegExp(`^[^\\p{C}]{1,${EVENT_TYPE_MAX_LENGTH}}$`, "u");

export function isValidDate(value: unknown): value is string {
  return (
    typeof value === "string" &&
    DATE_PATTERN.test(value) &&
    // The shape is not enough: "2026-02-30" matches the pattern and is not a
    // day. Unchecked, it reached the flow and the first attempt to format it
    // threw `RangeError: Invalid time value`, which unmounted the whole app —
    // a white screen from a mistyped URL (#2764, E4).
    isRealCalendarDate(value)
  );
}

export function isValidTime(value: unknown): value is string {
  return typeof value === "string" && TIME_PATTERN.test(value);
}

export function isValidId(value: unknown): value is string {
  return typeof value === "string" && ID_PATTERN.test(value);
}

export function isValidName(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && NAME_PATTERN.test(value);
}

export function isValidEventType(value: unknown): value is string {
  return typeof value === "string" && EVENT_TYPE_PATTERN.test(value);
}

/**
 * `a > b` for `HH:mm` / `H:mm`, compared semantically.
 *
 * Lexicographic comparison breaks on unpadded hours — "18:00" < "9:00" as
 * strings, although 18:00 is later in the day. Either side being the "0"
 * sentinel (Redux for "not chosen yet") yields false.
 */
export function timeIsAfter(a: string, b: string): boolean {
  if (a === "0" || b === "0") return false;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  if (
    !Number.isFinite(ah) ||
    !Number.isFinite(am) ||
    !Number.isFinite(bh) ||
    !Number.isFinite(bm)
  ) {
    return false;
  }
  return ah * 60 + am > bh * 60 + bm;
}

export function isValidGuestCount(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_GUESTS
  );
}

export function isValidStep(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAX_STEP
  );
}

/**
 * The highest step this query can actually restore — the fix for the defect on
 * the collector #2050, observed in #2630: *"the venue overview at
 * `?venueId=…&step=2` restores the step but not the selected entity"*.
 *
 * Restoring `step` unconditionally lands the customer on a screen whose inputs
 * were never hydrated: a blank overview with nothing to act on. That is
 * reachable today whenever a date fails validation, and renaming the keys would
 * make it happen to every old link at once — so the rename and this clamp ship
 * together (plan §5.2).
 *
 * The rule is the flow's own order of questions: where are you, when, which
 * object. A step is reachable only once the steps before it have their answers.
 * `objectId` is deliberately NOT required for the later steps — it resolves
 * asynchronously against a list that is fetched after step 1, so demanding it
 * here would throw the customer back every time the list is still in flight.
 * Its absence is handled where it is resolved, not here.
 */
export function clampStep(query: BookingQuery): number {
  const requested = query.step ?? 0;
  if (requested <= 0) return 0;

  const hasLocation = !!query.city;
  const hasDates = !!query.checkInDate;

  if (!hasLocation) return 0;
  // Without dates the only fillable step is the first one. This used to allow
  // step 1, on the belief that step 1 was where dates get entered — it is not:
  // step 0 (`LocationAndDurationSelectionStep`) asks for location AND dates
  // together, and step 1 is already the RESULTS. Letting a dateless link reach
  // it made the shell run the search with empty dates, producing
  // `/apartments/city/X/district/null/availabilities//` and an error screen
  // where the customer should simply have started the flow (QA #2764 E3).
  if (!hasDates) return 0;
  return requested;
}
